/**
 * Messages component.
 *
 * Main messaging interface for partner conversations. Displays conversation list
 * and message thread in a responsive layout. Handles conversation selection,
 * message sending, and conversation management (mark as read, delete).
 *
 * @module Messages
 */

import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle } from "lucide-react";
import { useIsLargeScreen } from "@/hooks/use-large-screen";
import { usePartnerFinderPath } from "@/hooks/usePartnerFinderPath";
import { useAuth } from "@/hooks/useAuth";
import { useConversations } from "@/hooks/useConversations";
import { ConversationListItem } from "./ConversationListItem";
import { MessageThread } from "./MessageThread";
import { useTranslation } from "react-i18next";

export default function Messages() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isLargeScreen = useIsLargeScreen();
  const paths = usePartnerFinderPath();
  const { user } = useAuth();

  // Get initial conversation ID from location state
  const initialConversationId =
    location.state?.partnerId && user?.id
      ? `conv-${location.state.partnerId}`
      : null;

  // Use custom hook for conversation management
  const {
    conversations,
    selectedConversationId,
    isLoading,
    selectedConversation,
    currentMessages,
    messagesRemaining,
    setSelectedConversationId,
    handleSendMessage,
    handleMarkUnread,
    handleDeleteConversation,
    handleConversationSelect: baseHandleConversationSelect,
  } = useConversations({
    userId: user?.id,
    initialConversationId,
  });

  // Wrap conversation select to handle mobile navigation
  const handleConversationSelect = (conversationId: string) => {
    baseHandleConversationSelect(conversationId);
    if (!isLargeScreen) {
      navigate(paths.messages, { replace: true });
    }
  };

  const handleBack = () => {
    setSelectedConversationId(null);
  };

  // Empty state
  if (isLoading && conversations.length === 0) {
    return (
      <Card className="p-8 lg:p-12 text-center">
        <p className="text-muted-foreground">
          {t("partnerFinder.messages.loadingMessages", {
            defaultValue: "Loading messages...",
          })}
        </p>
      </Card>
    );
  }

  if (conversations.length === 0) {
    return (
      <Card className="p-8 lg:p-12 text-center">
        <div className="space-y-4 max-w-md mx-auto">
          <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground" />
          <div>
            <h3 className="text-lg font-semibold mb-2">
              {t("partnerFinder.emptyStates.noConversations", {
                defaultValue: "No conversations yet",
              })}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t("partnerFinder.messages.noConversationsDesc", {
                defaultValue:
                  "Start a conversation with a partner to practice German together",
              })}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate(paths.find)} variant="outline">
                {t("partnerFinder.buttons.findPartners", {
                  defaultValue: "Find Partners",
                })}
              </Button>
              <Button onClick={() => navigate(paths.requestEdit)}>
                {t("partnerFinder.buttons.createRequest", {
                  defaultValue: "Create Request",
                })}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Desktop: Two-pane layout
  if (isLargeScreen) {
    return (
      <Card className="h-[600px] lg:h-[700px] flex">
        {/* Left: Conversation List */}
        <div className="w-80 border-r shrink-0 flex flex-col">
          <div className="p-4 border-b">
            <h2 className="font-semibold">
              {t("partnerFinder.messages.conversations", {
                defaultValue: "Conversations",
              })}
            </h2>
          </div>
          <ScrollArea className="flex-1">
            {conversations.map((conversation) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                isSelected={conversation.id === selectedConversationId}
                onClick={() => handleConversationSelect(conversation.id)}
                onMarkUnread={handleMarkUnread}
                onDelete={handleDeleteConversation}
              />
            ))}
          </ScrollArea>
        </div>

        {/* Right: Message Thread */}
        <div className="flex-1 flex flex-col min-w-0">
          <MessageThread
            conversation={selectedConversation}
            messages={currentMessages}
            onSendMessage={handleSendMessage}
            canSendMessage={
              selectedConversation?.partnerHasActiveRequest ?? true
            }
            messagesRemaining={messagesRemaining}
          />
        </div>
      </Card>
    );
  }

  // Mobile: Single view with navigation
  return (
    <div className="h-[calc(100dvh-13rem)] min-h-0 max-h-[44rem] sm:h-[calc(100dvh-15rem)]">
      {selectedConversationId ? (
        <Card className="h-full flex flex-col">
          <MessageThread
            conversation={selectedConversation}
            messages={currentMessages}
            onSendMessage={handleSendMessage}
            canSendMessage={
              selectedConversation?.partnerHasActiveRequest ?? true
            }
            messagesRemaining={messagesRemaining}
            onBack={handleBack}
          />
        </Card>
      ) : (
        <Card className="h-full flex flex-col">
          <div className="p-4 border-b">
            <h2 className="font-semibold">
              {t("partnerFinder.messages.conversations", {
                defaultValue: "Conversations",
              })}
            </h2>
          </div>
          <ScrollArea className="flex-1">
            {conversations.map((conversation) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                isSelected={false}
                onClick={() => handleConversationSelect(conversation.id)}
                onMarkUnread={handleMarkUnread}
                onDelete={handleDeleteConversation}
              />
            ))}
          </ScrollArea>
        </Card>
      )}
    </div>
  );
}
